import { uuidV7 } from '@luna/utils';
import { getEnv } from '@luna/env';

const NA_VERSION = 'N/A';
const SESSION_ID_STORAGE_KEY = 'sessionId';
const SESSION_ID_SEARCH_PARAM = 'sessionId';
const SINGLETON_EVENTS_KEY = 'tracked';

const getTrackedSingletonEvents = (): string[] => {
    return JSON.parse(sessionStorage.getItem(SINGLETON_EVENTS_KEY) || '[]');
};

export const hasTrackedSingletonEvent = (eventName: string): boolean => {
    return getTrackedSingletonEvents().includes(eventName);
};

export const trackSingletonEvent = (eventName: string): void => {
    sessionStorage.setItem(
        SINGLETON_EVENTS_KEY,
        JSON.stringify(Array.from(new Set([...getTrackedSingletonEvents(), eventName]))),
    );
};

export const deleteTrackingSessionData = (): void => {
    sessionStorage.removeItem(SESSION_ID_STORAGE_KEY);
    sessionStorage.removeItem(SINGLETON_EVENTS_KEY);
};

function generateSessionId(): string {
    return uuidV7();
}

function getSessionIdFromUrl(): string | undefined {
    return new URLSearchParams(window.location.search).get(SESSION_ID_SEARCH_PARAM) || undefined;
}

function preventSessionStartedTrackingEvent(): void {
    trackSingletonEvent('sessionStarted');
}

function removeSessionIdFromUrlSearch(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(SESSION_ID_SEARCH_PARAM);
    const { href } = url;
    window.history.replaceState(window.history.state, '', href);
}

function loadSessionIdFromStorage(): string | undefined {
    return sessionStorage.getItem(SESSION_ID_STORAGE_KEY) || undefined;
}

function saveSessionIdInStorage(sessionId: string): void {
    sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId.toUpperCase());
}

export function getVersion(): string {
    const { version } = getEnv();
    if (!version) {
        return NA_VERSION;
    }

    const splitBySegments = version.split('_');
    if (splitBySegments.length <= 1) {
        return version;
    }
    const branchWithVersion = splitBySegments[0];
    const branchWithVersionSplit = branchWithVersion.split('-');
    if (branchWithVersionSplit.length <= 1) {
        return version;
    }

    return branchWithVersionSplit.at(-1) ?? version;
}

export function initSession(): string {
    const sessionIdFromUrl = getSessionIdFromUrl();

    if (sessionIdFromUrl) {
        preventSessionStartedTrackingEvent();
        removeSessionIdFromUrlSearch();
        saveSessionIdInStorage(sessionIdFromUrl);
        return sessionIdFromUrl.toUpperCase();
    }

    const sessionIdFromSessionStorage = loadSessionIdFromStorage();
    if (sessionIdFromSessionStorage) {
        return sessionIdFromSessionStorage;
    }

    const sessionId = generateSessionId();
    saveSessionIdInStorage(sessionId);

    return sessionId.toUpperCase();
}

export const getSessionId = (): string => {
    return loadSessionIdFromStorage() ?? initSession();
};
